import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { LazyVideoSourceDirective } from '../../../directives/lazy-video-source.directive';
import { LightboxComponent } from '../lightbox/lightbox.component';
import {
  Gallery,
  GalleryItemView,
} from '../../../../features/projects/interfaces/project.interface';
import { buildGalleryViewModel, resolveShowLightboxThumbnails } from '../../../utils/gallery.utils';
import { GalleryLightboxState } from '../gallery-lightbox.state';

type ShowcaseTransitionPhase = 'idle' | 'prepared' | 'sliding';

@Component({
  selector: 'app-gallery-showcase',
  standalone: true,
  imports: [CommonModule, LightboxComponent, LazyVideoSourceDirective],
  templateUrl: './gallery-showcase.component.html',
  styleUrl: './gallery-showcase.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryShowcaseComponent implements OnChanges, OnDestroy {
  @Input() gallery!: Gallery;
  @Input() assetBase = '';
  @Input() videoBase = '';
  @Input() showGalleryHeading = true;

  readonly lightbox = new GalleryLightboxState();

  constructor(
    private cdr: ChangeDetectorRef,
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
  ) {}

  public activeIndex = 0;
  public outgoingIndex: number | null = null;
  public incomingIndex: number | null = null;
  public transitionPhase: ShowcaseTransitionPhase = 'idle';
  public isPlaying = false;
  public isPaused = false;
  public isInView = false;
  private autoplayTimer?: ReturnType<typeof setInterval>;
  private transitionTimer?: ReturnType<typeof setTimeout>;
  private transitionFrameId: number | null = null;
  private transitionStartFrameId: number | null = null;
  private revealObserver?: IntersectionObserver;

  public items: GalleryItemView[] = [];

  public get showLightboxThumbnails(): boolean {
    return resolveShowLightboxThumbnails(this.gallery, this.items.length);
  }

  public get headerLabel(): string {
    const label = this.gallery?.ctaLabel?.trim() || '';
    return label === 'Open premium gallery' ? '' : label;
  }

  public get displayOrderLabel(): string {
    return String((this.gallery?.displayOrder ?? 0) + 1).padStart(2, '0');
  }

  public get activeItem(): GalleryItemView | undefined {
    return this.items[this.activeIndex];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gallery'] || changes['assetBase'] || changes['videoBase']) {
      const viewModel = buildGalleryViewModel(this.gallery, {
        assetBase: this.assetBase,
        videoBase: this.videoBase,
      });
      this.items = viewModel.items;
      this.cancelSlideTransition();
      this.activeIndex = 0;
      this.lightbox.close();
      this.stopAutoplay();
      this.isInView = false;
      setTimeout(() => this.setupReveal(), 0);
    }
  }

  ngAfterViewInit(): void {
    this.setupReveal();
  }

  public goTo(index: number): void {
    const target = Math.min(Math.max(index, 0), this.items.length - 1);
    if (target === this.activeIndex || this.transitionPhase !== 'idle') return;

    this._restartAutoplay();

    if (this.prefersReducedMotion()) {
      this.activeIndex = target;
      this.cdr.markForCheck();
      return;
    }

    this.outgoingIndex = this.activeIndex;
    this.incomingIndex = target;
    this.transitionPhase = 'prepared';
    this.cdr.markForCheck();

    // Paint the incoming layer at its initial fade state before transitioning
    // both images. Two frames prevent a same-frame class swap from snapping.
    this.ngZone.runOutsideAngular(() => {
      this.transitionFrameId = window.requestAnimationFrame(() => {
        this.transitionFrameId = null;
        this.transitionStartFrameId = window.requestAnimationFrame(() => {
          this.transitionStartFrameId = null;
          this.ngZone.run(() => {
            this.activeIndex = target;
            this.transitionPhase = 'sliding';
            this.cdr.markForCheck();
          });

          this.transitionTimer = setTimeout(() => {
            this.ngZone.run(() => this.finishSlideTransition());
          }, 780);
        });
      });
    });
  }

  public prev(): void {
    const target = this.activeIndex > 0 ? this.activeIndex - 1 : this.items.length - 1;
    this.goTo(target);
  }

  public next(): void {
    const target = this.activeIndex < this.items.length - 1 ? this.activeIndex + 1 : 0;
    this.goTo(target);
  }

  public openAt(index: number): void {
    this.activeIndex = index;
    this.lightbox.open();
    this.cdr.markForCheck();
  }

  public startAutoplay(): void {
    this.stopAutoplay();
    this.isPlaying = true;
    this.isPaused = false;
    const interval = this.gallery?.autoplayInterval ?? 5000;
    this.autoplayTimer = setInterval(() => {
      this.next();
      this.cdr.markForCheck();
    }, interval);
  }

  public stopAutoplay(): void {
    if (this.autoplayTimer !== undefined) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
    }
    this.isPlaying = false;
    this.isPaused = false;
  }

  public pauseAutoplay(): void {
    if (this.isPlaying && this.autoplayTimer !== undefined) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = undefined;
      this.isPaused = true;
      this.cdr.markForCheck();
    }
  }

  public resumeAutoplay(): void {
    if (this.isPlaying && this.autoplayTimer === undefined) {
      this.isPaused = false;
      const interval = this.gallery?.autoplayInterval ?? 5000;
      this.autoplayTimer = setInterval(() => {
        this.next();
        this.cdr.markForCheck();
      }, interval);
      this.cdr.markForCheck();
    }
  }

  private _restartAutoplay(): void {
    if (this.isPlaying) {
      this.pauseAutoplay();
      this.resumeAutoplay();
    }
  }

  public onLightboxClose(): void {
    this.lightbox.close();
    this.cdr.markForCheck();
  }

  public onLightboxIndexChange(index: number): void {
    this.lightbox.setIndex(index);
    if (index !== this.activeIndex) {
      this.activeIndex = index;
    }
    this.cdr.markForCheck();
  }

  public ngOnDestroy(): void {
    this.revealObserver?.disconnect();
    this.cancelSlideTransition();
    this.lightbox.close();
    this.stopAutoplay();
  }

  private finishSlideTransition(): void {
    this.transitionTimer = undefined;
    this.outgoingIndex = null;
    this.incomingIndex = null;
    this.transitionPhase = 'idle';
    this.cdr.markForCheck();
  }

  private cancelSlideTransition(): void {
    if (this.transitionTimer !== undefined) {
      clearTimeout(this.transitionTimer);
      this.transitionTimer = undefined;
    }

    if (typeof window !== 'undefined' && this.transitionFrameId !== null) {
      window.cancelAnimationFrame(this.transitionFrameId);
    }

    if (typeof window !== 'undefined' && this.transitionStartFrameId !== null) {
      window.cancelAnimationFrame(this.transitionStartFrameId);
    }

    this.transitionFrameId = null;
    this.transitionStartFrameId = null;
    this.outgoingIndex = null;
    this.incomingIndex = null;
    this.transitionPhase = 'idle';
  }

  private prefersReducedMotion(): boolean {
    return typeof window === 'undefined'
      || (typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  private setupReveal(): void {
    this.revealObserver?.disconnect();
    if (typeof IntersectionObserver === 'undefined') {
      this.isInView = true;
      if (this.items.length > 1) {
        this.startAutoplay();
      }
      this.cdr.markForCheck();
      return;
    }

    const section = this.hostRef.nativeElement.querySelector<HTMLElement>('.showcase');
    if (!section) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            this.ngZone.run(() => {
              this.isInView = true;
              if (this.items.length > 1) {
                this.startAutoplay();
              }
              this.cdr.markForCheck();
            });

            this.revealObserver?.disconnect();
            this.revealObserver = undefined;
          });
        },
        { threshold: 0.24, rootMargin: '0px 0px -14% 0px' }
      );

      this.revealObserver.observe(section);
    });
  }
}
